import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Device } from '@twilio/voice-sdk';
import { TWILIO_CALL_STATE } from './twilio.constants';
import { getTwilioToken } from '../../../../services/twilio/twilio.service';

const TwilioCall = ({
  identity = null,
  onCallConnected,
  onCallFailed,
  onCallStateChange,
  children,
}) => {
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const [callState, setCallState] = useState(TWILIO_CALL_STATE.IDLE);
  const [twilioToken, setTwilioToken] = useState(null);
  const [fetchingTokken, setFetchingTokken] = useState(false);

  async function requestMicrophoneAccess() {
    try {
      // Ask for permission
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return true;
    } catch (err) {
      return false;
    }
  }

  useEffect(() => {
    onCallStateChange && onCallStateChange(callState);
  }, [callState, onCallStateChange]);

  const getTokken = async () => {
    if (fetchingTokken) return;
    if (!identity) {
      console.error('Identity is required in TwilioCall');
      return;
    }
    try {
      setFetchingTokken(true);
      const response = await getTwilioToken({
        identity,
        allowIncomingCalls: false,
        allowOutgoingCalls: true,
      });
      setTwilioToken(response?.data?.data?.token || null);
    } catch (error) {
      console.error('Failed to fetch Twilio token:', error);
    } finally {
      setFetchingTokken(false);
    }
  };

  useEffect(() => {
    setTimeout(async () => {
      await getTokken();
    }, 1500);
  }, []);

  useEffect(async () => {
    if (!twilioToken) {
      await getTokken();
      return;
    }

    const hasMicAccess = await requestMicrophoneAccess();
    if (!hasMicAccess) {
      alert('Please allow microphone access to make or receive calls.');
      return;
    }

    deviceRef.current = new Device(twilioToken, {
      codecPreferences: ['opus', 'pcmu'],
      fakeLocalDTMF: true,
      enableRingingState: true,
    });

    await deviceRef.current.register();

    return () => {
      deviceRef.current?.destroy();
      deviceRef.current = null;
      callRef.current = null;
    };
  }, [twilioToken]);

  const callHandler = async (params = {}) => {
    if (!deviceRef.current) return;
    if (deviceRef.current.state !== 'registered') {
      console.error('Device not ready, state:', deviceRef.current.state);
      return;
    }
    console.log('Attempting to make call');

    setCallState(TWILIO_CALL_STATE.CONNECTING);
    const call = await deviceRef.current.connect({ params });

    call.on('disconnect', () => {
      console.log('The call has been disconnected.');
      setCallState(TWILIO_CALL_STATE.DISCONNECTED);
    });
    call.on('accept', () => {
      console.log('The call has been connected.');
      setCallState(TWILIO_CALL_STATE.CONNECTED);
      onCallConnected && onCallConnected();
    });
    call.on('cancel', () => {
      console.log('The call has been canceled.');
      setCallState(TWILIO_CALL_STATE.IDLE);
    });
    call.on('reject', () => {
      console.log('The call has been rejected.');
      setCallState(TWILIO_CALL_STATE.IDLE);
    });
    call.on('error', error => {
      console.error('An error occurred during the call:', error);
      console.log('An error occurred during the call:', error);
      setCallState(TWILIO_CALL_STATE.FAILED);
      onCallFailed && onCallFailed(error);
    });

    callRef.current = call;
  };

  function hangup() {
    console.log('Attempting to hang up call');
    callRef.current.disconnect();
    callRef.current = null;
  }

  const mute = shouldMute => {
    callRef.current?.mute(shouldMute);
  };

  const accept = () => {
    callRef.current?.accept();
  };

  const reject = () => {
    callRef.current?.reject();
    setCallState(TWILIO_CALL_STATE.IDLE);
  };

  // Expose actions to children via render prop
  return (
    typeof children === 'function' &&
    children({
      callHandler,
      hangup,
      mute,
      accept,
      reject,
      callState,
    })
  );
};

TwilioCall.propTypes = {
  onCallConnected: PropTypes.func,
  onCallDisconnected: PropTypes.func,
  onCallFailed: PropTypes.func,
  onIncomingCall: PropTypes.func,
  children: PropTypes.func.isRequired,
};

export default TwilioCall;
